/**
 * AppProviders centralizes the provider stack in the exact order required
 * by the app. This preserves context dependencies while keeping App.tsx lean:
 * QueryClient → Redux store/persist → Tooltip → Amplify Authenticator →
 * PostHog → Feature Flags → View Settings → Focus Mode → Agent Chat →
 * Toaster → AuthErrorBoundary.
 */
import { Authenticator } from '@aws-amplify/ui-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'framer-motion';
import { ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PlatformChatSessionProvider } from '@/hooks/usePlatformChatSession';
import { queryClient } from '@/lib/query-client';
import AuthErrorBoundary from '../components/AuthErrorBoundary';
import FallbackSpinner from '../components/FallbackSpinner';
import { AgentChatProvider } from '../contexts/AgentChatContext';
import { FocusModeProvider } from '../contexts/FocusModeContext';
import { RecordingContextProvider } from '../contexts/RecordingContext';
import { ViewSettingsProvider } from '../contexts/ViewSettingsContext';
import { FeatureFlagsProvider } from '../providers/FeatureFlagsProvider';
import { PostHogProvider } from '../providers/PostHogProvider';
import { store, persistor } from '../redux/store';

type AppProvidersProps = {
  children: ReactNode;
};

/**
 * Centralized provider stack that mirrors the original App.tsx order.
 * The order is significant because downstream hooks (e.g., FeatureFlagsProvider)
 * depend on contexts defined earlier in the tree.
 */
export const AppProviders = ({ children }: AppProvidersProps) => (
  <MotionConfig reducedMotion="user" transition={{ duration: 0.18, ease: [0.215, 0.61, 0.355, 1] }}>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={<FallbackSpinner />} persistor={persistor}>
          <TooltipProvider>
            <Authenticator.Provider>
              <PostHogProvider>
                <FeatureFlagsProvider>
                  <ViewSettingsProvider>
                    <FocusModeProvider>
                      <RecordingContextProvider>
                        <PlatformChatSessionProvider>
                          <AgentChatProvider>
                            <Toaster />
                            <AuthErrorBoundary>{children}</AuthErrorBoundary>
                          </AgentChatProvider>
                        </PlatformChatSessionProvider>
                      </RecordingContextProvider>
                    </FocusModeProvider>
                  </ViewSettingsProvider>
                </FeatureFlagsProvider>
              </PostHogProvider>
            </Authenticator.Provider>
          </TooltipProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  </MotionConfig>
);
